import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import MegaMenu, { MegaMenuSection } from '../navigation/MegaMenu';

const Header = ({ announcementVisible = true }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  
  const megaMenuTop = announcementVisible ? 104 : 64;

  return (
    <header className="bg-white border-b border-gray-100 shadow-sm" style={{ zIndex: 50 }}>
      <div className="max-w-[1220px] mx-auto px-4">
        {/* Desktop Header */}
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/2026_logo.png" 
              alt="Student Signal" 
              className="h-[60px] w-auto"
              style={{ imageRendering: '-webkit-optimize-contrast' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {/* 1. College Search */}
            <Link
              to="/colleges"
              className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors"
            >
              College Search
            </Link>

            {/* 2. Scholarships */}
            <Link
              to="/scholarships"
              className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors"
            >
              Scholarships
            </Link>

            {/* 3. Tools */}
            <MegaMenu
              label="Tools"
              menuKey="tools"
              topPosition={megaMenuTop}
              staticFeaturedCard={
                <div className="flex flex-col h-full">
                  <div
                    className="rounded-lg mb-3 w-full flex-shrink-0"
                    style={{ backgroundColor: '#F3F4F6', height: '200px' }}
                    data-imagekit-slot="true"
                    data-menu="tools"
                    data-card="featured"
                  />
                  <span
                    style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1a5d3a', fontWeight: 600, display: 'block', marginBottom: '8px' }}
                  >
                    Featured Tool
                  </span>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a', marginBottom: '12px' }}>School Match Quiz</p>
                  <Link
                    to="/coming-soon/school-match-quiz"
                    style={{ backgroundColor: '#1a5d3a', color: 'white', fontSize: '13px', fontWeight: 600, display: 'block', textAlign: 'center', padding: '6px 16px', borderRadius: '6px', textDecoration: 'none', lineHeight: '1.5', marginTop: 'auto' }}
                  >
                    Try It Free
                  </Link>
                </div>
              }
            >
              <div className="grid grid-cols-3 gap-6">
                <MegaMenuSection
                  title="Search By"
                  links={[
                    { label: 'Online Colleges', href: '/coming-soon/online-colleges' },
                    { label: 'Transfer Students', href: '/coming-soon/transfer-students' },
                    { label: 'Military & Veteran Programs', href: '/coming-soon/military-programs' },
                    { label: 'International Students', href: '/coming-soon/international-students' },
                    { label: 'HBCUs', href: '/coming-soon/hbcus' },
                    { label: 'HSIs', href: '/coming-soon/hsis' },
                  ]}
                />
                <MegaMenuSection
                  title="Planning & Discovery"
                  links={[
                    { label: 'Career Finder', href: '/career-finder' },
                    { label: 'Compare Colleges', href: '/colleges' },
                    { label: 'Saved Colleges', href: '/dashboard' },
                    { label: 'School Match Quiz', href: '/coming-soon/school-match-quiz' },
                  ]}
                />
                {/* COL 3 — Coming Soon featured slot */}
                <div>
                  <div
                    style={{ height: '200px', width: '100%', backgroundColor: '#F3F4F6', borderRadius: '8px', marginBottom: '8px' }}
                    data-imagekit-slot="true"
                    data-menu="tools"
                    data-card="featured-2"
                  />
                  <span style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1a5d3a', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                    Coming Soon
                  </span>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>Career Finder Tool</p>
                </div>
              </div>
            </MegaMenu>

            {/* 4. Guidance (new) */}
            <MegaMenu
              label="Guidance"
              menuKey="guidance"
              topPosition={megaMenuTop}
              staticFeaturedCard={
                <div className="flex flex-col h-full">
                  {/* COL 4 — Featured panel */}
                  <div
                    className="rounded-lg mb-3 w-full flex-shrink-0"
                    style={{ backgroundColor: '#F9FAFB', minHeight: '200px' }}
                    data-imagekit-slot="true"
                    data-menu="guidance"
                    data-card="featured"
                  />
                  <span
                    className="font-semibold mb-1"
                    style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1a5d3a' }}
                  >
                    Featured Guide
                  </span>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a', textTransform: 'none', borderBottom: 'none', paddingBottom: 0, letterSpacing: 'normal', marginBottom: '12px', marginTop: 0 }}>
                    2025 Financial Aid &amp; Scholarship Trends
                  </h3>
                  <Link
                    to="/articles"
                    style={{ backgroundColor: '#1a5d3a', color: 'white', fontSize: '13px', fontWeight: 600, display: 'block', textAlign: 'center', padding: '6px 16px', borderRadius: '6px', textDecoration: 'none', lineHeight: '1.5' }}
                  >
                    Download Free
                  </Link>
                </div>
              }
            >
              {/* COL 1–3 inside children (col-span-2) */}
              <div className="grid grid-cols-3 gap-6" style={{ height: '100%' }}>
                {/* COL 1 — Insights (full-height sidebar, no card wrapper) */}
                <div style={{ background: '#f5f0e8', padding: '0 24px', height: '100%' }}>
                  <h3>Insights</h3>
                  <div>
                    <Link to="/articles" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Articles &amp; Advice</Link>
                    <Link to="/articles" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Videos &amp; Deep Dives</Link>
                    <Link to="/articles" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">PDF Downloads</Link>
                    <Link to="/application-tips" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Application Tips</Link>
                  </div>
                </div>

                {/* COL 2 — For Every Student (white) */}
                <div>
                  <h3>For Every Student</h3>
                  <div className="space-y-2">
                    <Link to="/guidance/first-generation" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">First Generation</Link>
                    <Link to="/guidance/international" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">International Students</Link>
                    <Link to="/guidance/athletes" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Athlete Guidance</Link>
                    <Link to="/guidance/transfer" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Transfer Students</Link>
                    <Link to="/guidance/military" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Military Programs</Link>
                    <Link to="/guidance/lgbt" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">LGBT Students</Link>
                    <Link to="/guidance/hbcus-hsis" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">HBCUs &amp; HSIs</Link>
                    <Link to="/guidance/parents" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Parents</Link>
                  </div>
                </div>

                {/* COL 3 — Admissions & Money (white) */}
                <div>
                  <h3>Admissions &amp; Money</h3>
                  <div className="space-y-2">
                    <Link to="/guidance/applications" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Application Guidance</Link>
                    <Link to="/guidance/essays" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Essay Help</Link>
                    <Link to="/guidance/financial-aid" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Financial Aid Basics</Link>
                    <a href="https://studentaid.gov" target="_blank" rel="noopener noreferrer" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">FAFSA Help</a>
                    <Link to="/scholarships" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Scholarship Guidance</Link>
                    <Link to="/guidance/affording-college" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">Affording College</Link>
                    <Link to="/guidance/sat-act-prep" className="block text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors">SAT/ACT Prep</Link>
                  </div>
                </div>
              </div>
            </MegaMenu>

            {/* 5. Ask Oz */}
            <MegaMenu
              label="Ask Oz"
              menuKey="ask_oz"
              topPosition={megaMenuTop}
              staticFeaturedCard={
                <div className="flex flex-col h-full">
                  {/* Vadoo video embed */}
                  <div style={{ position: 'relative', width: '100%', marginBottom: '12px' }}>
                    <div style={{
                      position: 'relative',
                      paddingBottom: '100%',
                      height: 0,
                      overflow: 'hidden',
                      borderRadius: '12px'
                    }}>
                      <iframe
                        src="https://api.vadoo.tv/iframe_test?id=LxEgC1dXYo0pJuu1fZ7DXuxSzCTwz0dR"
                        scrolling="no"
                        style={{
                          border: 0,
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          position: 'absolute',
                          overflow: 'hidden',
                          borderRadius: '12px'
                        }}
                        allowFullScreen={true}
                        allow="autoplay"
                        title="Meet Oz"
                      />
                    </div>
                  </div>
                  <span
                    style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1a5d3a', fontWeight: 600, display: 'block', marginBottom: '12px' }}
                  >
                    Message from Oz
                  </span>
                  <Link
                    to="/ask-oz"
                    style={{ backgroundColor: '#1a5d3a', color: 'white', fontSize: '13px', fontWeight: 600, display: 'block', textAlign: 'center', padding: '6px 16px', borderRadius: '6px', textDecoration: 'none', lineHeight: '1.5', width: '100%', boxSizing: 'border-box' }}
                  >
                    Message from Oz
                  </Link>
                </div>
              }
            >
              <div className="grid grid-cols-2 gap-8">
                {/* COL 1 — Find Your Path (gray sidebar, keep as is) */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3>Find Your Path</h3>
                  <div>
                    <Link to="/ask-oz">College Search Help</Link>
                    <Link to="/ask-oz">Scholarship Finder</Link>
                    <Link to="/ask-oz">Major Matching</Link>
                    <Link to="/ask-oz">Athlete Guidance</Link>
                    <Link to="/ask-oz">Career Readiness</Link>
                  </div>
                </div>
                {/* COL 2 — Ask Oz Anything (box wrapper matching Col 1) */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3>Ask Oz Anything</h3>
                  <p style={{ fontSize: '14px', color: '#2d2d2d', lineHeight: 1.7, marginBottom: '12px' }}>
                    Oz is your AI guide for college, scholarships, and what comes next. Powered by the most advanced AI — built for students. Register to unlock full access.
                  </p>
                  <div style={{ height: '80px', width: '100%', backgroundColor: '#f5f0e8', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
                    <p style={{ fontSize: '13px', color: '#999', textAlign: 'center', margin: 0 }}>Type your question...</p>
                  </div>
                  <Link
                    to="/ask-oz"
                    style={{ display: 'block', textAlign: 'center', padding: '6px 16px', fontSize: '13px', fontWeight: 600, border: '1px solid #1a5d3a', color: '#1a5d3a', borderRadius: '6px', textDecoration: 'none', width: '100%', boxSizing: 'border-box' }}
                  >
                    Chat with Oz
                  </Link>
                </div>
              </div>
            </MegaMenu>

            {/* 6. Media Hub */}
            <MegaMenu
              label="Media Hub"
              menuKey="media_hub"
              topPosition={megaMenuTop}
            >
              <div className="grid grid-cols-4 gap-4">
                {/* COL 1 — Featured Magazine (left anchor) */}
                <div style={{ padding: '0 20px' }}>
                  <div style={{ fontSize: '13px', color: '#1a5d3a', fontWeight: 700, textTransform: 'uppercase', borderBottom: '2px solid #f5a623', paddingBottom: '8px', marginBottom: '12px' }}>
                    Current Issue
                  </div>
                  <img
                    src="https://ik.imagekit.io/enrollforge/brands/studentsignal/Media%20Signal/Student%20Signal%20Magazine.png"
                    alt="StudentSignal Magazine Current Issue"
                    style={{ width: '100%', objectFit: 'cover' }}
                  />
                  <p style={{ fontSize: '14px', color: '#1a5d3a', fontWeight: 700, marginTop: '12px', marginBottom: '12px' }}>
                    Read Online Version
                  </p>
                  <Link
                    to="/dashboard"
                    style={{ backgroundColor: '#1a5d3a', color: 'white', padding: '6px 16px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', display: 'inline-block', borderRadius: '6px' }}
                  >
                    Member Access
                  </Link>
                </div>
                {/* COL 2 — Student Signal cream sidebar */}
                <div style={{ backgroundColor: '#f5f0e8', height: '100%', padding: '0 20px' }}>
                  <h3 style={{ fontSize: '13px', color: '#1a5d3a', fontWeight: 700, textTransform: 'uppercase' }}>Student Signal</h3>
                  <p style={{ fontSize: '12px', color: '#888', fontWeight: 500, textTransform: 'none', marginBottom: '8px' }}>Monthly Interactive Magazine</p>
                  <div>
                    <Link to="/magazine">Current Issue</Link>
                    <Link to="/magazine">Media Kit</Link>
                    <Link to="/magazine">Advertise With Us</Link>
                    <Link to="/magazine">Events</Link>
                  </div>
                </div>
                {/* COL 3 — Opportunity Knocks */}
                <div style={{ backgroundColor: 'white', padding: '0 20px' }}>
                  <h3 style={{ fontSize: '13px', color: '#1a5d3a', fontWeight: 700, textTransform: 'uppercase' }}>Opportunity Knocks</h3>
                  <p style={{ fontSize: '12px', color: '#888', fontWeight: 500, textTransform: 'none', marginBottom: '8px' }}>Weekly Newsletter</p>
                  <div>
                    <Link to="/newsletter">Latest Issue</Link>
                    <Link to="/newsletter">Subscribe Free</Link>
                    <Link to="/newsletter">Past Issues</Link>
                    <Link to="/newsletter">Read Latest Issue</Link>
                  </div>
                  <img
                    src="https://ik.imagekit.io/enrollforge/brands/studentsignal/Media%20Signal/SS-%20Opportunity%20Knoks%20-NL.png"
                    alt="Opportunity Knocks Newsletter"
                    style={{ width: '100%', maxHeight: '180px', objectFit: 'contain', display: 'block', marginTop: '10px' }}
                  />
                </div>
                {/* COL 4 — Student Door */}
                <div style={{ backgroundColor: 'white', padding: '0 20px' }}>
                  <h3 style={{ fontSize: '13px', color: '#1a5d3a', fontWeight: 700, textTransform: 'uppercase' }}>Student Door</h3>
                  <p style={{ fontSize: '12px', color: '#888', fontWeight: 500, textTransform: 'none', marginBottom: '8px' }}>Bi Monthly - Podcast</p>
                  <div>
                    <Link to="/podcast">Latest Episode</Link>
                    <Link to="/podcast">All Episodes</Link>
                    <Link to="/podcast">Watch on YouTube</Link>
                  </div>
                  <Link
                    to="/podcast"
                    style={{ display: 'block', fontSize: '13px', color: '#1a5d3a', fontWeight: 600, marginTop: '10px', textDecoration: 'none' }}
                  >
                    ▶ Latest Episode — The Student Door
                  </Link>
                  <div style={{ borderTop: '1px solid #f5a623', marginTop: '16px', paddingTop: '12px' }}>
                    <h4 style={{ fontSize: '12px', color: '#1a5d3a', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>Staff Weekly Gems</h4>
                    <p style={{ fontSize: '13px', color: '#2d2d2d', fontWeight: 600 }}>How To Ace The SAT</p>
                    <p style={{ fontSize: '13px', color: '#2d2d2d', fontWeight: 600 }}>Gig Jobs You Need</p>
                  </div>
                </div>
              </div>
            </MegaMenu>

            {user && (
              <Link
                to="/dashboard"
                className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors"
              >
                My Profile
              </Link>
            )}
          </nav>

          {/* Right Utilities */}
          <div className="flex items-center gap-3">            {user ? (
              <>
                <button
                  onClick={logout}
                  className="hidden lg:block text-sm font-medium text-gray-700 hover:text-red-600 transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="hidden lg:flex text-gray-700 hover:text-[#1a5d3a]">
                    Log In
                  </Button>
                </Link>

                <Link to="/signup">
                  <Button
                    size="sm"
                    className="hidden lg:flex bg-[#f5a623] hover:bg-[#e09000] text-white font-semibold"
                  >
                    Join Free
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <nav className="flex flex-col gap-3">
              <Link
                to="/colleges"
                className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                College Search
              </Link>
              <Link
                to="/ask-oz"
                className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Ask Oz
              </Link>
              <Link
                to="/scholarships"
                className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Scholarships
              </Link>
              <Link
                to="/articles"
                className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Articles & Guides
              </Link>
              <Link
                to="/magazine"
                className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Magazine
              </Link>
              <Link
                to="/podcast"
                className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Podcast
              </Link>
              <Link
                to="/newsletter"
                className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Newsletter
              </Link>
              {user && (
                <Link
                  to="/dashboard"
                  className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Profile
                </Link>
              )}
              {!user && (
                <>
                  <Link
                    to="/login"
                    className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Log In
                  </Link>
                  <Link
                    to="/signup"
                    className="text-sm font-medium text-gray-700 hover:text-[#1a5d3a] py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Join Free
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
